import type { CreateSnippetRequest, Snippet, SnippetCategory, Trip } from '@itin/shared'
import { zoneCityLabel } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { useLocale, useT } from '../lib/i18n.js'
import {
  appliesHere,
  narrowSnippets,
  SNIPPET_CATEGORIES,
  SnippetFilters,
  snippetDestinations,
} from './SnippetPicker.js'
import { Button, Card, ErrorNotice, Field, Input, Select, TextArea } from './ui.js'

/**
 * Ana's own texts — the paragraphs she writes for every trip.
 *
 * On the trip page beside the places library rather than on a screen of its own,
 * for the same reason: the moment she wants her CDG paragraph is the moment she
 * is building a Paris trip, and a library behind its own navigation item is a
 * library nobody opens.
 *
 * Unlike the places card this one can EDIT. That is not symmetry for its own
 * sake — the whole point of keeping prose is that the wording improves, and a
 * library you can only add to and delete from is one where the good version of a
 * paragraph is whichever trip she happened to write it best on. Editing here
 * never reaches an itinerary: every insert was a copy.
 */
export function SnippetsCard({ trip }: { trip: Trip }) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [destination, setDestination] = useState('')
  const [category, setCategory] = useState('')

  const snippets = useQuery({ queryKey: ['snippets'], queryFn: api.listSnippets })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['snippets'] })

  const create = useMutation({
    mutationFn: api.createSnippet,
    async onSuccess() {
      await invalidate()
      setAdding(false)
    },
  })

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateSnippetRequest }) =>
      api.updateSnippet(id, input),
    async onSuccess() {
      await invalidate()
      setEditingId(null)
    },
  })

  const remove = useMutation({ mutationFn: api.deleteSnippet, onSuccess: invalidate })

  const tripCity = zoneCityLabel(trip.timezone, locale)

  // The same function the in-day picker uses, so a text findable in one is
  // findable in the other by the same words.
  const shown = useMemo(
    () => narrowSnippets(snippets.data ?? [], { query, destination, category, here: tripCity }),
    [snippets.data, query, destination, category, tripCity],
  )
  const destinations = useMemo(() => snippetDestinations(snippets.data ?? []), [snippets.data])

  return (
    <Card
      testId="snippets-card"
      title={t('snippets.title')}
      collapsible
      actions={
        <Button variant="quiet" onClick={() => setAdding((v) => !v)}>
          {t('snippets.add')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-xs text-slate-500">{t('snippets.hint')}</p>

        {snippets.isError && <ErrorNotice error={snippets.error} />}
        {snippets.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {snippets.data?.length === 0 && !adding && (
          <p className="text-sm text-slate-500">{t('snippets.empty')}</p>
        )}

        {(snippets.data?.length ?? 0) > 0 && (
          <SnippetFilters
            query={query}
            destination={destination}
            category={category}
            destinations={destinations}
            onQuery={setQuery}
            onDestination={setDestination}
            onCategory={setCategory}
          />
        )}

        {(snippets.data?.length ?? 0) > 0 && shown.length === 0 && (
          <p className="text-sm text-slate-500">{t('snippets.noMatches')}</p>
        )}

        {(update.isError || remove.isError) && (
          <ErrorNotice error={update.error ?? remove.error} />
        )}

        <ul className="flex flex-col divide-y divide-slate-200">
          {shown.map((entry) =>
            editingId === entry.id ? (
              <li key={entry.id} className="py-3">
                <SnippetForm
                  existing={entry}
                  defaultDestination={tripCity}
                  busy={update.isPending}
                  onSubmit={(input) => update.mutate({ id: entry.id, input })}
                  onCancel={() => setEditingId(null)}
                />
              </li>
            ) : (
              <SnippetRow
                key={entry.id}
                entry={entry}
                here={appliesHere(entry, tripCity)}
                onEdit={() => setEditingId(entry.id)}
                onRemove={() => {
                  if (confirm(t('snippets.removeConfirm', { title: entry.title }))) {
                    remove.mutate(entry.id)
                  }
                }}
              />
            ),
          )}
        </ul>

        {adding && (
          <SnippetForm
            defaultDestination={tripCity}
            busy={create.isPending}
            error={create.error}
            onSubmit={(input) => create.mutate(input)}
            onCancel={() => setAdding(false)}
          />
        )}
      </div>
    </Card>
  )
}

function SnippetRow({
  entry,
  here,
  onEdit,
  onRemove,
}: {
  entry: Snippet
  here: boolean
  onEdit(): void
  onRemove(): void
}) {
  const t = useT()
  return (
    <li className="flex flex-wrap items-start justify-between gap-2 py-2">
      <div className="flex min-w-0 flex-col">
        <span className="flex items-center gap-2 font-medium">
          {entry.title}
          {here && (
            <span className="rounded bg-accent/20 px-1.5 py-0.5 text-xs font-medium text-slate-700">
              {t('snippets.here')}
            </span>
          )}
        </span>
        <span className="text-xs text-slate-500">
          {t(`snippets.category.${entry.category}`)} ·{' '}
          {entry.destination ?? t('snippets.anywhere')}
        </span>
        {/* Two lines. The point of the list is to find one, not to read them. */}
        <span className="line-clamp-2 text-xs text-slate-600">{entry.body}</span>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="quiet" onClick={onEdit}>
          {t('snippets.edit')}
        </Button>
        <Button variant="danger" onClick={onRemove}>
          {t('common.remove')}
        </Button>
      </div>
    </li>
  )
}

/**
 * One form, shared by "keep a new text" and "edit an existing one".
 *
 * `defaultDestination` prefills the trip's city when adding, because most of
 * what she writes while building a Paris trip is about Paris — but it is a text
 * input rather than a locked value, and clearing it is what files something
 * under "anywhere".
 */
function SnippetForm({
  existing,
  defaultDestination,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  existing?: Snippet
  defaultDestination: string
  busy: boolean
  error?: unknown
  onSubmit(input: CreateSnippetRequest): void
  onCancel(): void
}) {
  const t = useT()

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const destination = String(form.get('destination') ?? '').trim()
    onSubmit({
      title: String(form.get('title')),
      category: String(form.get('category')) as SnippetCategory,
      body: String(form.get('body')),
      // Empty means "applies everywhere", which is a null and not an empty
      // string — the filter and the ordering both read it as a real absence.
      destination: destination === '' ? null : destination,
    })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 rounded-md bg-slate-50 p-3">
      <Field label={t('snippets.field.title')} guidance="snippets.field.title.guidance">
        <Input name="title" required autoFocus maxLength={200} defaultValue={existing?.title} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('snippets.field.category')} guidance="snippets.field.category.guidance">
          <Select name="category" defaultValue={existing?.category ?? 'practical'}>
            {SNIPPET_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {t(`snippets.category.${value}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('snippets.field.destination')}
          guidance="snippets.field.destination.guidance"
        >
          <Input
            name="destination"
            maxLength={120}
            placeholder={t('snippets.anywhere')}
            defaultValue={existing ? (existing.destination ?? '') : defaultDestination}
          />
        </Field>
      </div>
      <Field label={t('snippets.field.body')} guidance="snippets.field.body.guidance">
        {/* Eight rows. This is the field the whole feature exists for, and a
            two-row box says "one line" for something that is four paragraphs. */}
        <TextArea name="body" required rows={8} maxLength={8_000} defaultValue={existing?.body} />
      </Field>
      {error ? <ErrorNotice error={error} /> : null}
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={busy}>
          {t('common.save')}
        </Button>
        <Button type="button" variant="quiet" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  )
}
